import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    useDisclosure,
    useColorModeValue,
    Portal
} from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Navbar from './Navbar';
import BackToTop from '../Common/BackToTop';
import ToastManagerComponent, { ToastManager } from '../Common/ToastManager';
import apiService from '../../services/apiService';
import { useNavigate } from 'react-router-dom';

/**
 * Admin Layout Component
 */
function AdminLayout({ children, userProfile, appConfig, onError }) {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [sidebarVisible, setSidebarVisible] = useState(true);
    const location = useLocation();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    
    // Issues management statistics for navbar/sidebar display
    const [issuesStats, setIssuesStats] = useState({
        totalIssues: 0,
        openIssues: 0,
        takenIssues: 0,
        clientsWithIssues: 0
    });
    const navigate = useNavigate();
    
    // Track count of newly toasted alerts for badge display
    const [newToastedAlertsCount, setNewToastedAlertsCount] = useState(() => {
        try {
            const stored = sessionStorage.getItem('newToastedAlertsCount');
            return stored ? parseInt(stored, 10) : 0;
        } catch {
            return 0;
        }
    });
    
    // notification tracking for smart toasting
    const [latestAlertToasted, setLatestAlertToasted] = useState(() => {
        try {
            const stored = sessionStorage.getItem('latestAlertToasted');
            if (stored) {
                const parsedIds = JSON.parse(stored);
                return new Set(parsedIds);
            }
            return new Set();
        } catch (error) {
            console.warn('Failed to restore latestAlertToasted from sessionStorage:', error);
            return new Set();
        }
    });
    
    const [allNotifications, setAllNotifications] = useState([]);
    
    // Use sessionStorage to persist first load state
    const [isFirstLoad, setIsFirstLoad] = useState(() => {
        try {
            const stored = sessionStorage.getItem('notificationFirstLoad');
            return stored === null ? true : JSON.parse(stored);
        } catch {
            return true;
        }
    });
    
    // Refs to track notification state
    const notificationQueue = useRef([]);
    const activeToasts = useRef(new Set());
    const maxConcurrentToasts = 3;
    const isProcessingNotifications = useRef(false);
    const isQueueProcessing = useRef(false);
    const notificationInterval = useRef(null);
    
    // Issues polling interval
    const issuesInterval = useRef(null);

    // Chakra UI theme-aware colors
    const mainBg = useColorModeValue('gray.50', 'gray.900');

    /**
     * Handle route changes to reset notification badge count
     */
    useEffect(() => {
        if (location.pathname === '/notifications') {
            setNewToastedAlertsCount(0);
            try {
                sessionStorage.setItem('newToastedAlertsCount', '0');
            } catch (error) {
                console.warn('Failed to reset newToastedAlertsCount in sessionStorage:', error);
            }
        }
        
        // Refresh issues stats when navigating to issues-related pages
        if (location.pathname === '/issues' || location.pathname === '/analytics') {
            fetchIssuesStats();
        }
    }, [location.pathname]);

    /**
     * Fetch issues statistics for dashboard/navbar display
     */
    const fetchIssuesStats = useCallback(async () => {
        try {
            const [issueCountsResponse, takenCountResponse] = await Promise.all([
                apiService.getIssueCountByClient(),
                apiService.getTakenIssueCount()
            ]);

            let stats = {
                totalIssues: 0,
                openIssues: 0,
                takenIssues: 0,
                clientsWithIssues: 0
            };

            if (issueCountsResponse.success) {
                const issueCounts = issueCountsResponse.data || {};
                const clientsWithIssues = Object.keys(issueCounts).length;
                const totalOpenIssues = Object.values(issueCounts).reduce((sum, count) => sum + count, 0);
                
                stats.openIssues = totalOpenIssues;
                stats.clientsWithIssues = clientsWithIssues;
            }

            if (takenCountResponse.success) {
                stats.takenIssues = takenCountResponse.data?.takenIssueCount || 0;
            }

            // Calculate total issues (open + taken, since taken issues are also open)
            stats.totalIssues = stats.openIssues;

            setIssuesStats(stats);

        } catch (error) {
            console.error('Failed to fetch issues statistics:', error);
        }
    }, []);

    /**
     * Start polling for issues statistics
     */
    const startIssuesPolling = useCallback(() => {
        // Initial load
        fetchIssuesStats();
        
        // Set up interval for polling every 60 seconds (less frequent than notifications)
        issuesInterval.current = setInterval(() => {
            fetchIssuesStats();
        }, 60000);
    }, [fetchIssuesStats]);

    /**
     * Stop issues polling
     */
    const stopIssuesPolling = useCallback(() => {
        if (issuesInterval.current) {
            clearInterval(issuesInterval.current);
            issuesInterval.current = null;
        }
    }, []);

    /**
     * Initialize layout with configuration
     */
    const initializeLayout = useCallback(async () => {
        try {
            if (appConfig) {
                apiService.initialize(appConfig);
            }
        } catch (error) {
            console.error('Layout initialization failed:', error);
            onError?.(error);
        }
    }, [appConfig, onError]);

    /**
     * Stop notification polling
     */
    const stopNotificationPolling = useCallback(() => {
        if (notificationInterval.current) {
            clearInterval(notificationInterval.current);
            notificationInterval.current = null;
        }
        isProcessingNotifications.current = false;
        isQueueProcessing.current = false;
        activeToasts.current.clear();
        notificationQueue.current = [];
    }, []);

    /**
     * process new notifications with backend read status
     */
    const processNewNotificationsEnhanced = useCallback(async (currentNotifications) => {
        try {
            const currentAlertIds = currentNotifications.map(n => n.id);
            const newAlertIds = currentAlertIds.filter(alertId => !latestAlertToasted.has(alertId));
            
            if (newAlertIds.length > 0) {
                const newAlerts = currentNotifications.filter(notification => 
                    newAlertIds.includes(notification.id) && !notification.isRead // Only toast unread notifications
                );

                // Sort new alerts by creation time (newest first)
                const sortedNewAlerts = newAlerts.sort((a, b) => 
                    new Date(b.createdAt) - new Date(a.createdAt)
                );

                // Show only the MOST RECENT new alert as a floating toast to avoid
                // flooding the screen when many alerts arrive at once (e.g. after the
                // tab has been left open overnight). The remaining new alerts are still
                // reflected in the badge count and remain available in the bell dropdown
                // and the Notifications page.
                const alertsToToast = sortedNewAlerts.slice(0, 1);

                // Add the most recent alert to the queue
                alertsToToast.forEach(alert => {
                    notificationQueue.current.push(alert);
                });

                // Start processing the queue
                if (!isQueueProcessing.current && alertsToToast.length > 0) {
                    processNotificationQueue();
                }

                // Increment badge count for ALL newly arrived alerts (not just the toasted one)
                if (sortedNewAlerts.length > 0) {
                    const newBadgeCount = newToastedAlertsCount + sortedNewAlerts.length;
                    setNewToastedAlertsCount(newBadgeCount);
                    
                    try {
                        sessionStorage.setItem('newToastedAlertsCount', newBadgeCount.toString());
                    } catch (error) {
                        console.warn('Failed to save newToastedAlertsCount to sessionStorage:', error);
                    }
                }

                // Sliding window approach for latestAlertToasted
                const updatedLatestAlertToasted = new Set();
                
                // Keep only alerts that are still in current results
                for (const alertId of latestAlertToasted) {
                    if (currentAlertIds.includes(alertId)) {
                        updatedLatestAlertToasted.add(alertId);
                    }
                }
                
                // Add only the NEW alerts that were just processed
                newAlertIds.forEach(alertId => {
                    updatedLatestAlertToasted.add(alertId);
                });
                
                setLatestAlertToasted(updatedLatestAlertToasted);
                
                try {
                    sessionStorage.setItem('latestAlertToasted', JSON.stringify(Array.from(updatedLatestAlertToasted)));
                } catch (error) {
                    console.warn('Failed to save updated latestAlertToasted to sessionStorage:', error);
                }
            } else {
                // Clean up latestAlertToasted even when no new alerts
                const cleanedLatestAlertToasted = new Set();
                
                for (const alertId of latestAlertToasted) {
                    if (currentAlertIds.includes(alertId)) {
                        cleanedLatestAlertToasted.add(alertId);
                    }
                }
                
                if (cleanedLatestAlertToasted.size !== latestAlertToasted.size) {
                    setLatestAlertToasted(cleanedLatestAlertToasted);
                    
                    try {
                        sessionStorage.setItem('latestAlertToasted', JSON.stringify(Array.from(cleanedLatestAlertToasted)));
                    } catch (error) {
                        console.warn('Failed to save cleaned latestAlertToasted to sessionStorage:', error);
                    }
                }
            }

        } catch (error) {
            console.error('Error processing new notifications:', error);
        }
    }, [latestAlertToasted, newToastedAlertsCount]);

    /**
     * Process notification queue with smart concurrent toast management
     */
    const processNotificationQueue = useCallback(async () => {
        if (isQueueProcessing.current || notificationQueue.current.length === 0) {
            return;
        }

        isQueueProcessing.current = true;

        while (notificationQueue.current.length > 0) {
            // Wait if we already have maximum concurrent toasts
            while (activeToasts.current.size >= maxConcurrentToasts) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            const notification = notificationQueue.current.shift();
            if (notification) {
                showSingleNotificationToastQueued(notification);
                
                if (notificationQueue.current.length > 0) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                }
            }
        }

        isQueueProcessing.current = false;
    }, []);

    /**
     * Show a single notification toast with queue management
     */
    const showSingleNotificationToastQueued = useCallback((notification) => {
        try {
            const getNotificationType = (status) => {
                switch (status?.toLowerCase()) {
                    case 'error':
                        return 'error'
                    case 'failed':
                        return 'error';
                    case 'critical':
                        return 'error'
                    case 'success':
                        return 'success';
                    case 'completed':
                        return 'success';
                    case 'warning':
                        return 'warning'
                    case 'pending':
                        return 'warning';
                    default:
                        return 'info';
                }
            };

            const category = notification.category || 'Alert';
            const message = notification.message || 'New notification received';
            const formattedMessage = `${category}: ${message}`;
            
            const type = getNotificationType(notification.status);
            const toastId = `alert_${notification.id}_${Date.now()}`;
            const duration = 5000;

            activeToasts.current.add(toastId);

            let toastResult;
            switch (type) {
                case 'error':
                    toastResult = ToastManager.showError(formattedMessage, {
                        duration: duration,
                        position: 'bottom-right',
                        id: toastId
                    });
                    break;
                case 'success':
                    toastResult = ToastManager.showSuccess(formattedMessage, {
                        duration: duration,
                        position: 'bottom-right',
                        id: toastId
                    });
                    break;
                case 'warning':
                    toastResult = ToastManager.showWarning(formattedMessage, {
                        duration: duration,
                        position: 'bottom-right',
                        id: toastId
                    });
                    break;
                default:
                    toastResult = ToastManager.showInfo(formattedMessage, {
                        duration: duration,
                        position: 'bottom-right',
                        id: toastId
                    });
                    break;
            }

            // Auto-remove from tracking after duration
            setTimeout(() => {
                if (activeToasts.current.has(toastId)) {
                    activeToasts.current.delete(toastId);
                }
            }, duration + 500);

        } catch (error) {
            console.error('Error showing queued notification toast:', error);
            if (activeToasts.current.size > 0) {
                const toastIds = Array.from(activeToasts.current);
                const oldestToast = toastIds[0];
                activeToasts.current.delete(oldestToast);
            }
        }
    }, []);

    /**
     * notification fetching with backend read status
     * Uses real user-specific read status from CAS_ALERT_USER_MAPPING table
     */
    const fetchNotificationsEnhanced = useCallback(async () => {
        if (isProcessingNotifications.current || !userProfile?.email) {
            return;
        }

        try {
            isProcessingNotifications.current = true;
            
            // Fetch latest notifications with user-specific read status
            const response = await apiService.getLatestAlertsWithUserReadStatus(50, null, userProfile.email);
            
            if (response.success && response.data) {
                const fetchedNotifications = Array.isArray(response.data) 
                    ? response.data 
                    : response.data.content || [];
                
                // Map API fields to component fields
                const processedNotifications = fetchedNotifications.map(notification => ({
                    ...notification,
                    id: notification.alertId || notification.id,
                    createdAt: notification.createdAtUtc || notification.createdAt,
                    isRead: notification.isRead || false // Backend provides real read status
                }));

                setAllNotifications(processedNotifications);
                
                // Get latest 3 for display in navbar dropdown
                const latestThreeForDisplay = processedNotifications.slice(0, 3);
                setNotifications(latestThreeForDisplay);
                
                // Calculate unread count from backend read status
                const unreadCountAll = processedNotifications.filter(n => !n.isRead).length;
                setUnreadCount(unreadCountAll);

                // Process new notifications
                if (isFirstLoad) {
                    // First load: Initialize without showing toasts
                    const currentAlertIds = new Set(processedNotifications.map(n => n.id));
                    setLatestAlertToasted(currentAlertIds);
                    
                    try {
                        sessionStorage.setItem('latestAlertToasted', JSON.stringify(Array.from(currentAlertIds)));
                    } catch (error) {
                        console.warn('Failed to save latestAlertToasted to sessionStorage:', error);
                    }
                    
                    setIsFirstLoad(false);
                    try {
                        sessionStorage.setItem('notificationFirstLoad', 'false');
                    } catch (error) {
                        console.warn('Failed to save notification first load state:', error);
                    }
                } else {
                    // Subsequent loads: Process new notifications
                    await processNewNotificationsEnhanced(processedNotifications);
                }
            } else {
                console.warn('API response unsuccessful or no data:', response);
            }
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            isProcessingNotifications.current = false;
        }
    }, [isFirstLoad, userProfile?.email, latestAlertToasted, processNewNotificationsEnhanced]);

    /**
     * Start polling for notifications with enhanced backend integration
     */
    const startNotificationPolling = useCallback(() => {
        if (!userProfile?.email) {
            return;
        }

        // Initial load
        fetchNotificationsEnhanced();
        
        // Set up interval for polling every 30 seconds
        const interval = 30000;
        notificationInterval.current = setInterval(() => {
            // Only fetch if queue is empty and not processing
            if (notificationQueue.current.length === 0 && 
                activeToasts.current.size === 0 && 
                !isQueueProcessing.current) {
                fetchNotificationsEnhanced();
            }
        }, interval);
    }, [fetchNotificationsEnhanced, userProfile?.email]);

    /**
     * Initialize layout and start background services including issues polling
     */
    useEffect(() => {
        initializeLayout();
        startNotificationPolling();
        startIssuesPolling(); // Start issues statistics polling
        
        return () => {
            stopNotificationPolling();
            stopIssuesPolling(); // Cleanup issues polling
        };
    }, [initializeLayout, startNotificationPolling, stopNotificationPolling, startIssuesPolling, stopIssuesPolling]);

    /**
     * Handle notification click with backend read status update
     */
    const handleNotificationClick = async (notification) => {
        try {
            // Mark as read if not already read
            if (!notification.isRead && userProfile?.email) {
                // Update local state immediately
                setNotifications(prev => 
                    prev.map(n => 
                        n.id === notification.id 
                            ? { ...n, isRead: true } 
                            : n
                    )
                );
                
                setAllNotifications(prev =>
                    prev.map(n =>
                        n.id === notification.id
                            ? { ...n, isRead: true }
                            : n
                    )
                );
                
                // Update unread count
                setUnreadCount(prev => Math.max(0, prev - 1));

                // Call backend API to mark as read
                try {
                    await apiService.markAlertAsReadForUser(notification.id, userProfile.email);
                } catch (apiError) {
                    console.error('Failed to mark notification as read in backend:', apiError);
                }
            }
            
            // Navigate to notifications page for full view
            // window.location.href = window.ADI_CONFIG?.app?.subDirectory + '/notifications';
            navigate('/notifications')
            
        } catch (error) {
            console.error('Failed to handle notification click:', error);
        }
    };

    /**
     * Handle sidebar open/close
     */
    const handleSidebarOpen = () => {
        if (window.innerWidth >= 1024) {
            setSidebarVisible(!sidebarVisible);
        } else {
            onOpen();
        }
    };

    const handleSidebarClose = () => {
        onClose();
    };

    /**
     * cleanup on user sign out
     * Also cleanup issues stats
     */
    useEffect(() => {
        const handleAuthStateChange = () => {
            if (!userProfile) {
                setLatestAlertToasted(new Set());
                setAllNotifications([]);
                setNotifications([]);
                setUnreadCount(0);
                setNewToastedAlertsCount(0);
                setIsFirstLoad(true);
                setIssuesStats({ //  Reset issues stats
                    totalIssues: 0,
                    openIssues: 0,
                    takenIssues: 0,
                    clientsWithIssues: 0
                });
                notificationQueue.current = [];
                activeToasts.current.clear();
                isQueueProcessing.current = false;
                stopNotificationPolling();
                stopIssuesPolling(); // Stop issues polling
                
                try {
                    sessionStorage.removeItem('notificationFirstLoad');
                    sessionStorage.removeItem('newToastedAlertsCount');
                    sessionStorage.removeItem('latestAlertToasted');
                } catch (error) {
                    console.warn('Failed to clear notification state:', error);
                }
            }
        };

        if (!userProfile && !isFirstLoad) {
            handleAuthStateChange();
        }
    }, [userProfile, isFirstLoad, stopNotificationPolling, stopIssuesPolling]);

    return (
        <Box minH="100vh" maxH="100vh" bg={mainBg} className="admin-layout-main" overflow="hidden">
            {/* Desktop Sidebar - Fixed positioning */}
            <Box
                display={{ base: 'none', lg: sidebarVisible ? 'block' : 'none' }}
                position="fixed"
                left={0}
                top={0}
                h="100vh"
                w="250px"
                zIndex={1001}
            >
                <Sidebar
                    variant="sidebar"
                    userProfile={userProfile}
                    appConfig={appConfig}
                    onClose={handleSidebarClose}
                    issuesStats={issuesStats}
                />
            </Box>

            {/* Mobile Sidebar Drawer */}
            <Sidebar
                isOpen={isOpen}
                onClose={handleSidebarClose}
                variant="drawer"
                userProfile={userProfile}
                appConfig={appConfig}
                issuesStats={issuesStats} // Pass issues statistics to mobile drawer
            />
            
            {/* Main Content Area */}
            <Box
                ml={{ base: 0, lg: sidebarVisible ? '250px' : 0 }}
                transition="margin-left 0.3s ease"
                h="100vh"
                position="relative"
                display="flex"
                flexDirection="column"
                className="admin-layout-content"
            >
                {/* Navigation Bar */}
                <Navbar
                    onOpen={handleSidebarOpen}
                    userProfile={userProfile}
                    notifications={notifications}
                    unreadCount={unreadCount}
                    newToastedAlertsCount={newToastedAlertsCount}
                    issuesStats={issuesStats}
                    sidebarVisible={sidebarVisible}
                    onNotificationClick={handleNotificationClick}
                    onNotificationsView={() => navigate('/notifications')}
                    appConfig={appConfig}
                />
                
                {/* Page Content Container */}
                <Box
                    as="main"
                    p={{ base: 4, md: 6 }}
                    pt={{ base: 6, md: 8 }}
                    flex="1"
                    bg={mainBg}
                    position="relative"
                    data-scroll-container="true"
                    overflowY="auto"
                    h="calc(100vh - 64px)"
                >
                    {children}
                </Box>
            </Box>
            
            {/* Back to Top Button */}
            <Portal>
                <Box className="back-to-top-button">
                    <BackToTop />
                </Box>
            </Portal>
            
            {/* Toast Manager for Enhanced Notifications */}
            <ToastManagerComponent />
        </Box>
    );
}

export default AdminLayout;